import { useEffect, useMemo, useState } from 'react';
import {
  Archive,
  CheckCircle2,
  ClipboardSignature,
  Download,
  FileCheck2,
  Fingerprint,
  LockKeyhole,
  PackageCheck,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSession } from '@/hooks/useSession';
import {
  getInspectionExport,
  getValidationRequirements,
} from '../api/validationApi';
import { ManagementReviewDialog } from '../components/validation/ManagementReviewDialog';
import { ValidationEvidenceDialog } from '../components/validation/ValidationEvidenceDialog';
import { ValidationRequirementsTable } from '../components/validation/ValidationRequirementsTable';
import {
  useCreateInspectionExport,
  useCreateManagementReview,
  useInspectionExports,
  useManagementReviews,
  useRecordValidationEvidence,
  useValidationRequirements,
  useValidationSummary,
} from '../hooks/useValidation';
import type {
  InspectionExport,
  ManagementReview,
  ValidationRequirement,
  ValidationRequirementFilters,
  ValidationRequirementStatus,
} from '../types/validation.types';

const PAGE_LENGTH = 25;
const MODULE_OPTIONS = [
  { value: 'document_control', label: 'Document Control' },
  { value: 'change_control', label: 'Change Control' },
  { value: 'capa', label: 'CAPA' },
  { value: 'compliance', label: 'Cross-Module Compliance' },
  { value: 'training', label: 'Training Management' },
];

function formatUtc(value: string | null | undefined) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en-CA', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(new Date(value)) + ' UTC';
}

function useValidationTableState() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<ValidationRequirementStatus | 'all'>('all');
  const [module, setModule] = useState('');
  const [sortKey, setSortKey] = useState<ValidationRequirementFilters['sortKey']>('requirementCode');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState({
    search: '',
    statementSearch: '',
    implementationSearch: '',
  });

  const columnSearches = useMemo(
    () => ({
      search: columnFilters.requirementCode?.trim() || '',
      statementSearch: columnFilters.statement?.trim() || '',
      implementationSearch: columnFilters.implementationReference?.trim() || '',
    }),
    [columnFilters],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedColumnFilters(columnSearches);
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [columnSearches]);

  const filters = useMemo<ValidationRequirementFilters>(() => ({
    status,
    module: module || undefined,
    search: debouncedColumnFilters.search || undefined,
    statementSearch: debouncedColumnFilters.statementSearch || undefined,
    implementationSearch: debouncedColumnFilters.implementationSearch || undefined,
    pageNumber: page,
    pageLength: PAGE_LENGTH,
    sortKey,
    sortDirection,
  }), [debouncedColumnFilters, module, page, sortDirection, sortKey, status]);

  const onSort = (key: ValidationRequirementFilters['sortKey']) => {
    if (key === sortKey) setSortDirection((current) => current === 1 ? -1 : 1);
    else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  return {
    page,
    setPage,
    status,
    setStatus: (value: ValidationRequirementStatus | 'all') => {
      setStatus(value);
      setPage(0);
    },
    module,
    setModule: (value: string) => {
      setModule(value === 'all' ? '' : value);
      setPage(0);
    },
    sortKey,
    sortDirection,
    columnFilters,
    setColumnFilter: (key: string, value: string) => {
      setColumnFilters((current) => ({ ...current, [key]: value }));
    },
    clearFilters: () => {
      setColumnFilters({});
      setStatus('all');
      setModule('');
      setPage(0);
    },
    filters,
    onSort,
  };
}

async function fetchAllRequirements(filters: ValidationRequirementFilters) {
  const requirements: ValidationRequirement[] = [];
  let pageNumber = 0;
  let total: number;
  do {
    const batch = await getValidationRequirements({
      ...filters,
      pageNumber,
      pageLength: 100,
    });
    requirements.push(...batch.requirements);
    total = batch.total;
    pageNumber += 1;
  } while (requirements.length < total);
  return requirements;
}

function downloadFrozenPackage(exportRecord: InspectionExport) {
  if (!exportRecord.manifest) {
    toast.error('The frozen inspection manifest could not be loaded.');
    return;
  }
  const envelope = {
    packageId: exportRecord.id,
    generatedAtUtc: exportRecord.createdAt,
    cutoffAtUtc: exportRecord.cutoffAt,
    generatedBy: exportRecord.generatedByName,
    payloadSha256: exportRecord.payloadSha256,
    manifest: exportRecord.manifest,
  };
  const blob = new Blob([JSON.stringify(envelope, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `eqms-inspection-${exportRecord.id}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function RequirementReadinessSpine({
  total,
  verified,
  mapped,
  gaps,
}: {
  total: number;
  verified: number;
  mapped: number;
  gaps: number;
}) {
  const segments = [
    ...Array.from({ length: verified }, () => 'verified' as const),
    ...Array.from({ length: mapped }, () => 'mapped' as const),
    ...Array.from({ length: gaps }, () => 'gap' as const),
  ];
  while (segments.length < total) segments.push('mapped');

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4 text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
        <span>Requirement readiness spine</span>
        <span>{verified}/{total} verified</span>
      </div>
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${Math.max(total, 1)}, minmax(0, 1fr))` }}
        aria-label={`${verified} verified, ${mapped} mapped, ${gaps} gaps across ${total} requirements`}
      >
        {(segments.length ? segments : ['mapped']).map((state, index) => (
          <div
            key={`${state}-${index}`}
            className={`h-2 rounded-full ${
              state === 'verified'
                ? 'bg-teal-400'
                : state === 'gap'
                  ? 'bg-amber-400'
                  : 'bg-cyan-700'
            }`}
            title={`Requirement ${index + 1}: ${state}`}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewCard({ review }: { review: ManagementReview }) {
  const metrics = review.metrics;
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{review.title}</CardTitle>
            <div className="mt-1 text-xs text-slate-500">
              {review.periodStart} → {review.periodEnd} · frozen {formatUtc(review.cutoffAt)}
            </div>
          </div>
          <Badge className="border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-50">
            <Fingerprint className="mr-1 h-3.5 w-3.5" /> Approved
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Validation', `${metrics.validation.verified}/${metrics.validation.total} verified`],
            ['Documents', `${metrics.documents.effective}/${metrics.documents.total} effective`],
            ['Quality events', `${metrics.qualityEvents.openCapa} open CAPA`],
            ['Training', `${metrics.training.nonCompliant} non-compliant`],
          ].map(([label, value]) => (
            <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
              <div className="mt-1 text-sm font-medium text-slate-900">{value}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-4 text-sm lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Observations</div>
            <p className="mt-1 whitespace-pre-wrap leading-5 text-slate-700">{review.observations}</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Decisions</div>
            <p className="mt-1 whitespace-pre-wrap leading-5 text-slate-700">{review.decisions}</p>
          </div>
        </div>
        {review.actionItems.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Actions</div>
            <ul className="mt-2 space-y-1 text-sm text-slate-700">
              {review.actionItems.map((item) => <li key={item}>• {item}</li>)}
            </ul>
          </div>
        )}
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 text-sm text-indigo-950">
          <div className="font-medium">
            {review.signature.signerName} · {review.signature.meaning}
          </div>
          <div className="mt-1 text-xs">{formatUtc(review.signature.signedAt)}</div>
          <div className="mt-2 italic">“{review.signature.reason}”</div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ValidationReadinessPage() {
  const { user } = useSession();
  const tableState = useValidationTableState();
  const [selectedRequirement, setSelectedRequirement] = useState<ValidationRequirement | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const summaryQuery = useValidationSummary();
  const requirementsQuery = useValidationRequirements(tableState.filters);
  const reviewsQuery = useManagementReviews();
  const exportsQuery = useInspectionExports();
  const evidenceMutation = useRecordValidationEvidence();
  const reviewMutation = useCreateManagementReview();
  const exportMutation = useCreateInspectionExport();

  const summary = summaryQuery.data;
  const requirements = requirementsQuery.data?.requirements || [];
  const defaultUsername = user?.username || user?.email || '';

  const createAndDownloadPackage = async () => {
    try {
      const result = await exportMutation.mutateAsync();
      downloadFrozenPackage(result.export);
    } catch {
      // The mutation owns the user-facing error toast.
    }
  };

  const downloadExistingPackage = async (record: InspectionExport) => {
    try {
      const result = await getInspectionExport(record.id);
      downloadFrozenPackage(result.export);
    } catch {
      toast.error('Could not retrieve the frozen inspection package');
    }
  };

  return (
    <div className="min-h-full bg-slate-50">
      <section
        className="overflow-hidden bg-[#0b172a] text-white"
        style={{
          backgroundImage:
            'linear-gradient(rgba(14,116,144,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(14,116,144,0.12) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      >
        <div className="mx-auto max-w-[1600px] px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <div className="mb-3 flex items-center gap-2">
                <Badge className="border-cyan-300/20 bg-cyan-300/10 text-cyan-100 hover:bg-cyan-300/10">
                  <PackageCheck className="mr-1 h-3.5 w-3.5" /> eQMS · Validation & Inspection Readiness
                </Badge>
                <span className="font-mono text-xs text-slate-400">PHASE 4</span>
              </div>
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                Every claim should have a trail.
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base">
                Trace the specification to implemented controls, bind objective results to accountable
                signatures, and freeze inspection packages without rewriting history.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="border-slate-600 bg-slate-900/70 text-white hover:bg-slate-800 hover:text-white"
                onClick={() => setReviewDialogOpen(true)}
              >
                <ClipboardSignature className="mr-2 h-4 w-4" /> Capture management review
              </Button>
              <Button
                className="bg-cyan-500 text-slate-950 hover:bg-cyan-400"
                disabled={exportMutation.isPending}
                onClick={createAndDownloadPackage}
              >
                <Archive className="mr-2 h-4 w-4" />
                {exportMutation.isPending ? 'Freezing…' : 'Freeze inspection package'}
              </Button>
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-slate-700/80 bg-slate-950/75 p-4 backdrop-blur">
            {summaryQuery.isLoading ? (
              <Skeleton className="h-10 w-full bg-slate-700" />
            ) : (
              <RequirementReadinessSpine
                total={summary?.requirements.total || 0}
                verified={summary?.requirements.verified || 0}
                mapped={summary?.requirements.mapped || 0}
                gaps={summary?.requirements.gaps || 0}
              />
            )}
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {[
                ['Mapped', summary?.requirements.mapped || 0, 'text-cyan-300'],
                ['Verified', summary?.requirements.verified || 0, 'text-teal-300'],
                ['Open gaps', summary?.requirements.gaps || 0, 'text-amber-300'],
                ['Signed evidence', summary?.evidenceCount || 0, 'text-white'],
              ].map(([label, value, tone]) => (
                <div key={String(label)}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {label}
                  </div>
                  <div className={`mt-1 text-xl font-semibold tabular-nums ${tone}`}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1600px] space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid gap-3 md:grid-cols-3">
          {[
            {
              label: 'Requirement map',
              value: `${summary?.requirements.total || 0} controls`,
              detail: 'Every named FSD requirement has a source and acceptance criterion.',
              icon: FileCheck2,
            },
            {
              label: 'Management evidence',
              value: `${summary?.managementReviewCount || 0} snapshots`,
              detail: `Latest approved: ${formatUtc(summary?.lastReviewAt)}`,
              icon: ClipboardSignature,
            },
            {
              label: 'Frozen exports',
              value: `${summary?.inspectionExportCount || 0} packages`,
              detail: 'Each source manifest is preserved with a SHA-256 digest.',
              icon: LockKeyhole,
            },
          ].map((item) => (
            <Card key={item.label} className="border-slate-200 shadow-sm">
              <CardContent className="flex gap-4 p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-800">
                  <item.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">{item.label}</div>
                  <div className="mt-1 text-lg font-semibold text-slate-950">{item.value}</div>
                  <div className="mt-1 text-xs leading-5 text-slate-500">{item.detail}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="traceability" className="space-y-4">
          <TabsList className="h-auto flex-wrap justify-start bg-white p-1 shadow-sm">
            <TabsTrigger value="traceability">Requirements traceability</TabsTrigger>
            <TabsTrigger value="reviews">Management reviews</TabsTrigger>
            <TabsTrigger value="exports">Inspection packages</TabsTrigger>
          </TabsList>

          <TabsContent value="traceability" className="space-y-4">
            <div className="flex flex-wrap gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Select
                value={tableState.status}
                onValueChange={(value) =>
                  tableState.setStatus(value as ValidationRequirementStatus | 'all')}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All readiness states</SelectItem>
                  <SelectItem value="mapped">Mapped · awaiting evidence</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="gap">Open gaps</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tableState.module || 'all'} onValueChange={tableState.setModule}>
                <SelectTrigger className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modules</SelectItem>
                  {MODULE_OPTIONS.map((module) => (
                    <SelectItem key={module.value} value={module.value}>{module.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
                <CheckCircle2 className="h-4 w-4 text-teal-600" />
                Pass verifies · <ShieldAlert className="h-4 w-4 text-amber-600" /> Fail preserves a gap
              </div>
            </div>

            <ValidationRequirementsTable
              requirements={requirements}
              total={requirementsQuery.data?.total || 0}
              page={tableState.page}
              pageLength={PAGE_LENGTH}
              sortKey={tableState.sortKey}
              sortDirection={tableState.sortDirection}
              columnFilters={tableState.columnFilters}
              status={tableState.status}
              module={tableState.module}
              isLoading={requirementsQuery.isLoading}
              isRefreshing={requirementsQuery.isFetching}
              onColumnFilterChange={tableState.setColumnFilter}
              onClearFilters={tableState.clearFilters}
              onSort={tableState.onSort}
              onPageChange={tableState.setPage}
              onRefresh={() => void requirementsQuery.refetch()}
              onRecordEvidence={setSelectedRequirement}
              onExportAll={() => fetchAllRequirements(tableState.filters)}
            />
          </TabsContent>

          <TabsContent value="reviews" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Approved management snapshots</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Metrics and decisions remain tied to the exact cutoff and signer.
                </p>
              </div>
              <Button onClick={() => setReviewDialogOpen(true)}>
                <ClipboardSignature className="mr-2 h-4 w-4" /> Capture review
              </Button>
            </div>
            {reviewsQuery.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : reviewsQuery.data?.reviews.length ? (
              <div className="space-y-4">
                {reviewsQuery.data.reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            ) : (
              <Card className="border-dashed border-slate-300">
                <CardContent className="py-12 text-center">
                  <ClipboardSignature className="mx-auto h-8 w-8 text-slate-300" />
                  <div className="mt-3 font-medium text-slate-800">No management review has been captured.</div>
                  <p className="mt-1 text-sm text-slate-500">
                    Capture the first signed cross-module evidence snapshot.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="exports" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Frozen inspection packages</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Downloads reproduce the server-frozen manifest, source counts, cutoff, and digest.
                </p>
              </div>
              <Button
                onClick={createAndDownloadPackage}
                disabled={exportMutation.isPending}
              >
                <Archive className="mr-2 h-4 w-4" />
                {exportMutation.isPending ? 'Freezing…' : 'Freeze new package'}
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              {exportsQuery.isLoading ? (
                <div className="p-5"><Skeleton className="h-24 w-full" /></div>
              ) : exportsQuery.data?.exports.length ? (
                <div className="divide-y divide-slate-100">
                  {exportsQuery.data.exports.map((record) => (
                    <div
                      key={record.id}
                      className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between"
                    >
                      <div className="flex gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                          <PackageCheck className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-mono text-sm font-semibold text-slate-900">{record.id}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {formatUtc(record.cutoffAt)} · {record.generatedByName}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-600">
                            <span>{record.requirementCount} requirements</span>
                            <span>·</span>
                            <span>{record.evidenceCount} evidence records</span>
                            <span>·</span>
                            <span>{record.signatureCount} signatures</span>
                            <span>·</span>
                            <span>{record.managementReviewCount} reviews</span>
                          </div>
                          <div className="mt-2 break-all font-mono text-[10px] text-slate-400">
                            SHA-256 {record.payloadSha256}
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => downloadExistingPackage(record)}>
                        <Download className="mr-2 h-4 w-4" /> Download frozen manifest
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-10 text-center text-sm text-slate-500">
                  No inspection package has been frozen.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <ValidationEvidenceDialog
        requirement={selectedRequirement}
        defaultUsername={defaultUsername}
        isPending={evidenceMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setSelectedRequirement(null);
        }}
        onConfirm={async (input) => {
          if (!selectedRequirement) return;
          try {
            await evidenceMutation.mutateAsync({
              requirementId: selectedRequirement.id,
              input,
            });
            setSelectedRequirement(null);
          } catch {
            // Leave the dialog open so the signer can correct the failed request.
          }
        }}
      />

      <ManagementReviewDialog
        open={reviewDialogOpen}
        defaultUsername={defaultUsername}
        isPending={reviewMutation.isPending}
        onOpenChange={setReviewDialogOpen}
        onConfirm={async (input) => {
          try {
            await reviewMutation.mutateAsync(input);
            setReviewDialogOpen(false);
          } catch {
            // Leave the dialog open so the signer can correct the failed request.
          }
        }}
      />
    </div>
  );
}
